import {Box, Field, FormControl, InputLabel, Select, MenuItem, Stack, FormHelperText, TextArea, Button} from "@digitalreality/ui";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useState, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { Inline } from "@digitalreality/ui/src/index.js";
import { useTranslation } from "react-i18next";
import React from "react";
import { CountriesCodes, Timezones, locales } from "../../Helpers/Constants";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useAppState } from "../../StateStore/Store";
import { isProjectUnique } from "../../Services/ProjectsService";
import { RestApiResponse } from "../../Models/RestApiResponse";
import { TimezoneItem } from "../../Models/TimezoneItem";
import { Logo } from "../../Models/Logo";
import { InformationIcon } from "@digitalreality/ui-icons";
import CustomTooltip from "../../Components/CustomTooltip";
import { useEffect } from "react";

const logoPlaceholder = require("../../Images/noImageLarge.png");

dayjs.extend(utc);
dayjs.extend(timezone);

export default function GeneralDetails({ onSubmit, formRef }: any) {
    const { t } = useTranslation();
    const { state } = useAppState();
    const [selectedImage, setSelectedImage] = useState<string | null | undefined>(state.projectModal.project?.logo?.data);
    const logoInput = useRef<HTMLInputElement>(null);

    var logoFile: Logo = state.projectModal.project?.logo ?? ({} as Logo);

    const {
        control,
        formState: { errors },
        setError,
        clearErrors,
        setValue,
        getValues,
        handleSubmit,
    } = useForm({
        defaultValues: state.projectModal.project,
        mode: "onBlur",
    });

    const dateIsValid = (date: any) => dayjs(date).isValid();

    const isUnique = (value: string) => {
        if (value && value !== state.projectModal.project?.projectName) {
            isProjectUnique(value).then((response: RestApiResponse) => {
                if (!response.data) {
                    setError("projectName", {
                        type: "isUnique",
                        message: t("Texts.ProjectUniqueValidation"),
                    });
                }
            });
        }
        return true;
    };

    const validateDatePicker = (date: any) => {
        if (date != undefined && dateIsValid(date)) {
            return true;
        } else {
            return t("Texts.InvalidDateValidation");
        }
    };

    const validateSeasonEnd = (date: Date | undefined) => {
        if (dayjs(date).isBefore(dayjs(getValues("seasonStart")), "day")) {
            return t("Texts.ValidationDateTimeEnd");
        }
        return true;
    };

    const validatePreSeason = (date: Date | undefined) => {
        if (dayjs(date).isBefore(dayjs(getValues("seasonStart")), "day") || dayjs(date).isAfter(dayjs(getValues("seasonEnd")), "day")) {
            return t("Texts.ValidationBetweenStartAndEnd");
        }
    };

    const buildCountriesList = () => {
        var countries: { code: string; name: string }[] = [];
        CountriesCodes.map((code) => {
            if (code.toLowerCase() !== "undefined") {
                countries.push({ code: code, name: t("Texts.c" + code) });
            }
        });

        return countries.sort((a, b) => a.name.localeCompare(b.name)).map((country) => (
            <MenuItem key={country.code} value={country.code}>
                {country.name}
            </MenuItem>
        ));
    };

    const buildTimezonesList = () => {
        return Timezones.map((timezone: TimezoneItem) => (
            <MenuItem key={timezone.offset} value={timezone.offset}>
                {timezone.label + " (UTC " + (timezone.offset >= 0 ? "+" : "") + timezone.offset + ")"}
            </MenuItem>
        ));
    };

    const addInfoTooltip = (title: string) => {
        return (
            <CustomTooltip arrow title={title} placement="bottom-end">
                <InformationIcon
                    sx={{
                        color: "grey",
                        alignSelf: "self-start",
                        marginTop: "13px",
                        fontSize: "1.75rem",
                    }}/>
            </CustomTooltip>
        );
    };

    useEffect(() => {
        if (!state.projectModal.project?.seasonStart) {
            setValue("seasonStart", dayjs().month(10).date(1).hour(1).minute(0).toDate());
        }
        if (!state.projectModal.project?.seasonEnd) {
            setValue("seasonEnd", dayjs().add(1, 'year').month(4).date(31).hour(1).minute(1).toDate());
        }
        if (!state.projectModal.project?.preSeason) {
            setValue("preSeason", dayjs().month(10).date(1).hour(23).minute(0).toDate());
        }
      }, [setValue, state.projectModal.project?.seasonStart, state.projectModal.project?.seasonEnd, state.projectModal.project?.preSeason]);

    return (
        <form
            id="clientForm"
            onSubmit={handleSubmit(onSubmit)}
            ref={formRef}
            autoComplete="off">
            <Stack direction="row">
                <Stack width="300px" marginRight="81px">
                    <Box
                        sx={{
                        width: "300px",
                        height: "250px",
                        background: selectedImage ? "white" : "#4C4C4C",
                        borderRadius: "6px",
                        objectFit: "scale-down",
                        }}>
                        <img
                            style={{
                                maxWidth: "300px",
                                maxHeight: "250px",
                                width: "100%",
                                height: "100%",
                                objectFit: "scale-down",
                                backgroundPosition: "center",
                            }}
                            src={selectedImage ? "data:image/png;base64," + selectedImage : logoPlaceholder}/>
                    </Box>
                    <input
                        type="file"
                        accept="image/*"
                        hidden
                        ref={logoInput}
                        onChange={(event) => {
                            if (event.target.files && event.target.files.length > 0) {
                                const file = event.target.files[0];

                                logoFile = {
                                    ...logoFile,
                                    filename: file.name,
                                    size: file.size,
                                    extension: file.name.split(".").pop() ?? "",
                                    mimetype: file.type,
                                };
                                const reader = new FileReader();

                                reader.readAsDataURL(file);

                                reader.onload = () => {
                                    logoFile.data = reader.result?.toString().split(",")[1];
                                    setValue("logo", logoFile);
                                    setSelectedImage(logoFile.data);
                                };
                            }
                        }}/>
                    <Inline
                        justifyContent="end"
                        gap={5}
                        marginTop="24px"
                        borderRadius="6px">
                        <Button
                            variant="outlined"
                            onClick={() => logoInput.current?.click()}>
                            {t("Texts.UploadLogo")}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setSelectedImage(undefined);
                                setValue("logo", {} as Logo);
                            }}>
                            {t("Texts.Delete")}
                        </Button>
                    </Inline>
                </Stack>

                <Stack marginRight="81px" gap={6}>
                    <Controller
                        name="customer"
                        control={control}
                        rules={{
                            required: t("Texts.ValidationRequired") as string,
                            maxLength: { value: 256, message: t("Texts.LengthValidation") },
                        }}
                        render={({ field }) => (
                            <Field
                                {...field}
                                label={t("Texts.Customer")}
                                error={!!errors.customer}
                                helperText={errors.customer?.message}/>
                        )}/>

                    <Controller
                        name="vendor"
                        control={control}
                        rules={{
                            required: t("Texts.ValidationRequired") as string,
                            maxLength: { value: 256, message: t("Texts.LengthValidation") },
                        }}
                        render={({ field }) => (
                            <Field
                                {...field}
                                label={t("Texts.SalesOrganization")}
                                error={!!errors.vendor}
                                helperText={errors.vendor?.message}/>
                        )}/>

                    <Controller
                        name="projectName"
                        control={control}
                        rules={{
                            required: t("Texts.ValidationRequired") as string,
                            maxLength: { value: 128, message: t("Texts.LengthValidation") },
                            pattern: {
                                value: /^[a-z]+[0-9]*$/,
                                message: t("Texts.DomainDescription"),
                            },
                            minLength: {
                                value: 4,
                                message: t("Texts.DomainDescription"),
                            },
                            validate: isUnique,
                        }}
                        render={({ field: { ref, ...field } }) => (
                        <Field
                            {...field}
                            label={t("Texts.ProjectNameUrl")}
                            disabled={state.projectModal.project?.dealerProjectId != undefined && state.projectModal.project.dealerProjectId > 0}
                            error={!!errors.projectName}
                            helperText={errors.projectName?.message}/>
                        )}/>

                    <Controller
                        name="language"
                        control={control}
                        rules={{ required: t("Texts.ValidationRequired") as string }}
                        render={({ field }) => (
                        <FormControl error={!!errors.language}>
                            <InputLabel>{t("Texts.Language")}</InputLabel>
                            <Select
                                {...field}
                                defaultValue={state.projectModal.project?.language ?? ""}
                                label={t("Texts.Language")}
                                onChange={(event) => {
                                    setValue("language", event.target.value);
                                }}>
                                {locales.map((locale) => (
                                    <MenuItem key={locale.id} value={locale.id}>
                                        {locale.label}
                                    </MenuItem>
                                ))}
                            </Select>
                            <FormHelperText>{errors.language?.message}</FormHelperText>
                        </FormControl>
                        )}/>

                    <Controller
                        name="country"
                        control={control}
                        rules={{ required: t("Texts.ValidationRequired") as string }}
                        render={({ field }) => (
                        <FormControl error={!!errors.country}>
                            <InputLabel>{t("Texts.Country")}</InputLabel>
                            <Select
                                {...field}
                                label={t("Texts.Country")}
                                onChange={(event) => {setValue("country", event.target.value)}}>
                                <MenuItem key="undefined" value="Undefined">
                                    Undefined
                                </MenuItem>
                                {buildCountriesList()}
                            </Select>
                            <FormHelperText>{errors.country?.message}</FormHelperText>
                        </FormControl>
                        )}/>

                    {/* The functionality is needed, but for the moment until the backend is ready we hid the functionality from the user
                    <Controller
                        name="dateformat"
                        control={control}
                        render={({ field }) => <Field {...field} label="Time Format" />}
                    />*/}

                    <Controller
                        name="timeZone"
                        control={control}
                        rules={{ required: t("Texts.ValidationRequired") as string }}
                        render={({ field }) => (
                        <FormControl error={!!errors.timeZone}>
                            <InputLabel>{"Time Zone"}</InputLabel>
                            <Select
                                {...field}
                                label={"Project Time Zone"}
                                onChange={(event) => {setValue("timeZone", Number(event.target.value))}}>
                                {buildTimezonesList()}
                            </Select>
                            <FormHelperText>{errors.timeZone?.message}</FormHelperText>
                        </FormControl>
                        )}/>

                    {/*<Controller
                        name="timeZone"
                        control={control}
                        rules={{pattern: {value: /^[+-]?[0-9]+$/, message: "Only numbers"}}}
                        render={({ field }) => (
                            <Field
                                {...field}
                                label="Project timezone offset (UTC ±)"
                                error={!!errors.timeZone}
                                helperText={errors.timeZone?.message}/>
                        )}/>*/}
                </Stack>

                <Stack gap={5}>
                    <Inline gap={2} alignItems="baseline">
                        <Controller
                            name="seasonStart"
                            control={control}
                            rules={{validate: validateDatePicker}}
                            render={({ field }) => (
                                <DatePicker
                                    {...field}
                                    label={t("Texts.SeasonStart")}
                                    timezone="UTC"
                                    value={state.projectModal.project?.seasonStart != null ? dayjs(state.projectModal.project.seasonStart) : undefined}
                                    defaultValue={dayjs().month(10).date(1)}
                                    views={["year", "month", "day"]}
                                    slotProps={{textField: {helperText: errors.seasonStart?.message, error: !!errors.seasonStart}}}
                                    onChange={(newValue) => {
                                        if (newValue == null) {
                                            setError("seasonStart", {
                                            type: "required",
                                            message: t("Texts.ValidationRequired") as string,
                                            });
                                        }
                                        var message: string | boolean = validateDatePicker(newValue);
                                        if (message == true) {
                                            clearErrors("seasonStart");
                                            setValue("seasonStart", dayjs.utc(newValue).startOf("day").toDate());
                                        } else {
                                            setValue("seasonStart", undefined);
                                            setError("seasonStart", {
                                                type: "dateInvalid",
                                                message: typeof message == "string" ? message : "",
                                            });
                                        }
                                    }}/>
                            )}/>
                        {addInfoTooltip(t("Texts.SeasonStartInfo"))}

                        <Controller
                            name="seasonEnd"
                            control={control}
                            rules={{
                                validate: {
                                    isValid: validateDatePicker,
                                    isInRange: validateSeasonEnd,
                                },
                            }}
                            render={({ field }) => (
                                <DatePicker
                                    {...field}
                                    label={t("Texts.SeasonEnd")}
                                    timezone="UTC"
                                    value={state.projectModal.project?.seasonEnd != null ? dayjs(state.projectModal.project.seasonEnd) : undefined}
                                    defaultValue={dayjs().add(1, 'year').month(4).date(31)}
                                    views={["year", "month", "day"]}
                                    minDate={dayjs.utc(getValues("seasonStart"))}
                                    maxDate={dayjs(getValues("seasonStart")).add(1, "year")}
                                    slotProps={{
                                        textField: {
                                            helperText: errors.seasonEnd?.message,
                                            error: !!errors.seasonEnd,
                                        },
                                    }}
                                    onError={(error) => {
                                        if (error == "minDate") {
                                            setError("seasonEnd", {
                                                type: "required",
                                                message: t("Texts.ValidationDateTimeEnd") as string,
                                            });
                                        }
                                        if (error == "maxDate") {
                                            setError("seasonEnd", {
                                                type: "required",
                                                message: t("Texts.SeasonLimitValidation"),
                                            });
                                        }
                                    }}
                                    onChange={(newValue) => {
                                        if (newValue == null) {
                                            setError("seasonEnd", {
                                                type: "required",
                                                message: t("Texts.ValidationRequired") as string,
                                            });
                                        }
                                        var message: string | boolean = validateDatePicker(newValue);
                                        if (message == true) {
                                            clearErrors("seasonEnd");
                                            setValue(
                                                "seasonEnd",
                                                dayjs
                                                .utc(newValue)
                                                .endOf("day")
                                                .startOf("minute") // to round seconds and miliseconds to 0
                                                .toDate()
                                            );
                                        } else {
                                            setValue("seasonEnd", undefined);
                                            setError("seasonEnd", {
                                                type: "dateInvalid",
                                                message: typeof message == "string" ? message : "",
                                            });
                                        }
                                    }}/>
                                )}/>
                        {addInfoTooltip(t("Texts.SeasonEndInfo"))}
                    </Inline>

                    <Inline gap={2} alignItems="baseline">
                        <Controller
                            name="preSeason"
                            control={control}
                            rules={{
                                validate: {
                                    isValid: validateDatePicker,
                                    isInRange: validatePreSeason,
                                },
                            }}
                            render={({ field }) => (
                                <DatePicker
                                    {...field}
                                    label={t("Texts.MainSeasonStart")}
                                    timezone="UTC"
                                    value={state.projectModal.project?.preSeason != null ? dayjs(state.projectModal.project.preSeason) : undefined}
                                    defaultValue={dayjs().month(10).date(1)}
                                    views={["year", "month", "day"]}
                                    minDate={dayjs.utc(getValues("seasonStart"))}
                                    maxDate={dayjs.utc(getValues("seasonEnd"))}
                                    slotProps={{
                                        textField: {
                                            helperText: errors.preSeason?.message,
                                            error: !!errors.preSeason,
                                        },
                                    }}
                                    onError={(error) => {
                                        if (error == "minDate" || error == "maxDate") {
                                            setError("preSeason", {
                                                type: "rangeError",
                                                message: t("Texts.ValidationBetweenStartAndEnd") as string});
                                        }
                                    }}
                                    onChange={(newValue) => {
                                        if (newValue == null) {
                                            setError("preSeason", {
                                                type: "required",
                                                message: t("Texts.ValidationRequired") as string,
                                            });
                                        }
                                        var message: string | boolean = validateDatePicker(newValue);
                                        if (message == true) {
                                            clearErrors("preSeason");
                                            setValue("preSeason", dayjs.utc(newValue).startOf("day").toDate());
                                        } else {
                                            setValue("preSeason", undefined);
                                            setError("preSeason", {
                                                type: "dateInvalid",
                                                message: typeof message == "string" ? message : "",
                                            });
                                        }
                                    }}/>
                            )}/>
                        {addInfoTooltip(t("Texts.MainSeasonStartInfo"))}
                    </Inline>

                    <Controller
                        name="description"
                        control={control}
                        render={({ field }) => (
                            <TextArea
                                {...field}
                                sx={{ maxWidth: "544px" }}
                                label={t("Texts.Description")}
                                maxLength={256}/>
                        )}/>
                </Stack>
            </Stack>
        </form>
    );
}
